import React from 'react';
import HeroCarousel from '../../components/HeroCarousel';
import { Link } from 'react-router-dom';
import { Globe, Target, Users, Calendar, Clock, MapPin, Mail, Image as ImageIcon, Share2, ArrowUpRight, Heart } from 'lucide-react';

// ARCHETYPE 12: MENFA — Inspired by MENFA CANADA Fundraising Party flyer
// Hot pink/magenta, African textile patterns, ribbon banners, family warmth, bold outlined text
const StageTemplate = ({ association: a, activities, theme: t, onShare }) => {
  const C = {
    white: '#FFFFFF',
    pink: '#D81B60',
    pinkLight: '#FCE4EC',
    dark: '#2D1810',
    black: '#1A1A1A',
    gold: t.secondary || '#FFB300',
    textile: '#3E2723',
  };

  // CSS pattern to simulate African textile border
  const textilePattern = {
    backgroundImage: `
      repeating-linear-gradient(45deg, ${C.pink}15 0px, ${C.pink}15 2px, transparent 2px, transparent 8px),
      repeating-linear-gradient(-45deg, ${C.gold}12 0px, ${C.gold}12 2px, transparent 2px, transparent 8px),
      linear-gradient(${C.textile}, ${C.textile})
    `,
  };

  return (
    <div style={{ backgroundColor: C.white, color: C.black }} className="min-h-screen">
      {/* Navbar — white with pink accent */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b shadow-sm" style={{ backgroundColor: C.white, borderColor: `${C.pink}30` }} data-testid="association-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={a.logo_url || a.image_url} alt="" className="w-10 h-10 rounded-full object-cover border-2" style={{ borderColor: C.pink }} />
            <span className="text-xl font-black tracking-tight" style={{ fontFamily: "'Bangers', cursive", color: C.pink, letterSpacing: '0.04em' }}>{a.short_name}</span>
          </div>
          <div className="hidden md:flex gap-6">
            {['About', 'Heritage', 'Leadership', 'Events', 'Gallery'].map(s => (
              <a key={s} href={`#${s.toLowerCase()}`} className="text-sm font-bold uppercase tracking-wider hover:opacity-70 transition-colors" style={{ color: C.dark }}>{s}</a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onShare} className="p-2 hover:opacity-70" data-testid="share-btn"><Share2 className="w-4 h-4" /></button>
            <Link to="/" className="text-xs font-bold px-3 py-1.5 rounded-full text-white" style={{ backgroundColor: C.pink }} data-testid="cca-return-badge">CCA Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero — Flyer style with textile border feel */}
      <section id="about" className="pt-14" data-testid="association-hero">
        {/* Textile-pattern top banner */}
        <div className="py-3" style={textilePattern} />

        {/* Main hero — photo with pink frame, bold text */}
        <div className="relative py-12 sm:py-16 px-4" style={{ backgroundColor: C.white }}>
          <div className="max-w-4xl mx-auto text-center">
            {/* Bold outlined header — flyer style */}
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black leading-[0.9] mb-2" style={{
              fontFamily: "'Bangers', cursive",
              color: C.white,
              WebkitTextStroke: `2px ${C.dark}`,
              letterSpacing: '0.04em',
              textShadow: `3px 3px 0 ${C.pink}40`,
            }} data-testid="association-name">
              {a.short_name}
            </h1>
            <p className="text-xl sm:text-2xl font-bold italic mb-6" style={{ fontFamily: "'Lora', serif", color: C.pink }}>
              {a.name}
            </p>

            {/* Region badge */}
            <div className="inline-block px-5 py-1.5 rounded-full mb-8 shadow" style={{ backgroundColor: C.pink, color: C.white }}>
              <span className="text-xs font-black uppercase tracking-widest">{a.region} Region</span>
            </div>

            {/* Central photo with pink frame — like the flyer */}
            <div className="max-w-2xl mx-auto">
              <div className="p-2 rounded-xl" style={{ backgroundColor: C.pink }}>
                {a.hero_images?.length > 0
                  ? <HeroCarousel images={a.hero_images} className="w-full aspect-[16/10] rounded-lg overflow-hidden" />
                  : <div className="w-full aspect-[16/10] rounded-lg flex items-center justify-center" style={{ backgroundColor: `${C.pink}20` }}>
                      <img src={a.logo_url || a.image_url} alt="" className="w-24 h-24 object-contain opacity-40" />
                    </div>
                }
              </div>
            </div>

            {/* "Our Family" ribbon banner — like the flyer */}
            <div className="inline-block mt-6 px-8 py-2 relative" style={{ backgroundColor: C.black }}>
              <div className="absolute left-0 top-0 w-0 h-0" style={{ borderTop: '18px solid transparent', borderBottom: '18px solid transparent', borderRight: `12px solid ${C.black}`, transform: 'translateX(-100%)' }} />
              <div className="absolute right-0 top-0 w-0 h-0" style={{ borderTop: '18px solid transparent', borderBottom: '18px solid transparent', borderLeft: `12px solid ${C.black}`, transform: 'translateX(100%)' }} />
              <span className="text-lg font-black uppercase tracking-widest text-white" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em' }}>Our Family</span>
            </div>
          </div>
        </div>

        {/* Textile-pattern divider */}
        <div className="py-2" style={textilePattern} />
      </section>

      {/* Cultural highlights strip on pink */}
      {a.cultural_highlights && (
        <section className="py-8 px-6" style={{ backgroundColor: C.pinkLight }} data-testid="cultural-highlights-banner">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="w-5 h-5 mx-auto mb-3" style={{ color: C.pink }} />
            <p className="text-base font-medium leading-relaxed italic" style={{ fontFamily: "'Lora', serif", color: C.dark }}>
              {a.cultural_highlights}
            </p>
          </div>
        </section>
      )}

      {/* About + Mission */}
      <section className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.white }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.pink, color: C.white }}>About Us</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.dark, letterSpacing: '0.03em' }}>Who We Are</h2>
          </div>
          <p className="text-lg leading-relaxed text-center max-w-3xl mx-auto mb-10" style={{ color: `${C.dark}aa`, fontFamily: "'Quicksand', sans-serif" }} data-testid="association-description">{a.description}</p>
          {a.origin && <p className="text-center text-sm mb-10 flex items-center justify-center gap-2" style={{ color: `${C.dark}55` }}><Globe className="w-4 h-4" style={{ color: C.pink }} />{a.origin}</p>}

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 sm:p-8 rounded-2xl border-2" style={{ borderColor: C.pink, backgroundColor: `${C.pink}06` }}>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3" style={{ backgroundColor: C.pink, color: C.white }}>Our Mission</div>
              <p className="leading-relaxed" style={{ color: `${C.dark}88`, fontFamily: "'Quicksand', sans-serif" }} data-testid="association-mission">{a.mission}</p>
            </div>
            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: C.textile, color: C.white }}>
              <h3 className="text-lg font-bold mb-4 uppercase tracking-wider" style={{ fontFamily: "'Bangers', cursive", color: C.gold }}>Core Values</h3>
              <div className="flex flex-wrap gap-2" data-testid="association-values">
                {(a.values || []).map((v, i) => (
                  <span key={i} className="px-4 py-2 rounded-full text-sm font-bold border border-white/25 text-white" style={{ backgroundColor: `${C.pink}40` }}>{v}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage — pink light background */}
      <section id="heritage" className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.pinkLight }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.pink, color: C.white }}>Heritage</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.dark }}>Cultural Heritage</h2>
          </div>
          <p className="text-lg leading-relaxed text-center" style={{ color: `${C.dark}77`, fontFamily: "'Quicksand', sans-serif" }} data-testid="cultural-background">{a.cultural_background || a.cultural_highlights}</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[{ t: 'Traditional Attire', d: 'Members proudly wear traditional garments during ceremonies.' }, { t: 'Traditional Dance', d: 'Cultural dances passed down through generations.' }, { t: 'Cuisine', d: 'Traditional dishes shared at community gatherings.' }].map((item, i) => (
              <div key={i} className="p-5 rounded-2xl border text-center" style={{ borderColor: `${C.pink}25`, backgroundColor: C.white }}>
                <h4 className="font-bold text-lg mb-2" style={{ fontFamily: "'Bangers', cursive", color: C.pink }}>{item.t}</h4>
                <p className="text-sm" style={{ color: `${C.dark}66`, fontFamily: "'Quicksand', sans-serif" }}>{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section id="leadership" className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.white }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.pink, color: C.white }}>Leadership</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.dark }}>Our Leaders</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="rounded-2xl overflow-hidden border-2 text-center" style={{ borderColor: C.pink }}>
              <div className="h-2" style={{ backgroundColor: C.pink }} />
              <div className="p-6">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden" style={{ border: `3px solid ${C.pink}` }}>
                  {a.president_photo_url ? <img src={a.president_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: C.pinkLight }}><span className="text-3xl font-bold" style={{ color: C.pink }}>{(a.president_name || '?')[0]}</span></div>}
                </div>
                <h4 className="font-bold text-lg" style={{ fontFamily: "'Quicksand', sans-serif" }}>{a.president_name || 'Vacant'}</h4>
                <span className="inline-block px-3 py-0.5 rounded-full text-xs font-bold mt-1 text-white" style={{ backgroundColor: C.pink }}>President</span>
              </div>
            </div>
            {(a.leadership || []).map((l, i) => (
              <div key={l.id || i} className="rounded-2xl overflow-hidden border text-center" style={{ borderColor: `${C.pink}25` }}>
                <div className="p-6">
                  <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden" style={{ backgroundColor: C.pinkLight }}>
                    {l.photo_url ? <img src={l.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><span className="text-2xl" style={{ color: `${C.dark}30` }}>{(l.name || '?')[0]}</span></div>}
                  </div>
                  <h4 className="font-bold">{l.name}</h4>
                  <p className="text-sm mt-1" style={{ color: C.pink }}>{l.role}</p>
                </div>
              </div>
            ))}
            {(!a.leadership || a.leadership.length === 0) && ['Vice President', 'Secretary'].map(r => (
              <div key={r} className="rounded-2xl border border-dashed p-6 text-center" style={{ borderColor: `${C.pink}20` }}>
                <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: C.pinkLight }}><Users className="w-6 h-6" style={{ color: `${C.dark}20` }} /></div>
                <h4 className="font-semibold" style={{ color: `${C.dark}30` }}>{r}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events — on dark textile background */}
      <section id="events" className="relative">
        <div className="py-2" style={textilePattern} />
        <div className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.textile, color: C.white }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.gold }}>Events</h2>
            </div>
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map(ev => (
                  <div key={ev.id} className="p-5 rounded-2xl flex flex-col sm:flex-row gap-4" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} data-testid={`event-${ev.id}`}>
                    <div className="flex-shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: C.pink, color: C.white }}>
                      <span className="text-xl font-black">{ev.date?.split('-')[2]}</span>
                      <span className="text-[10px] uppercase font-bold">{ev.date && new Date(ev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-white">{ev.title}</h4>
                      <p className="text-sm text-white/60 mt-1">{ev.description}</p>
                      <div className="flex gap-3 mt-2 text-xs text-white/40">
                        {ev.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ev.time}</span>}
                        {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                <Calendar className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <p className="text-white/40">No events scheduled yet.</p>
              </div>
            )}
          </div>
        </div>
        <div className="py-2" style={textilePattern} />
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.white }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.pink, color: C.white }}>Gallery</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.dark }}>Photo Gallery</h2>
          </div>
          {a.gallery_images?.length > 0 ? (
            <div className="columns-2 md:columns-3 gap-4 space-y-4">
              {a.gallery_images.map((img, i) => (
                <div key={i} className="break-inside-avoid rounded-2xl overflow-hidden p-1 shadow-md" style={{ backgroundColor: C.pink }} data-testid={`gallery-image-${i}`}>
                  <img src={img.url} alt={img.caption || ''} className="w-full object-cover rounded-xl hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 rounded-2xl border-2 border-dashed" style={{ borderColor: `${C.pink}20` }}>
              <ImageIcon className="w-12 h-12 mx-auto mb-3" style={{ color: `${C.dark}15` }} />
              <p style={{ color: `${C.dark}40` }}>Gallery coming soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Videos */}
      <section className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: C.pinkLight }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: C.pink, color: C.white }}>Videos</span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Bangers', cursive", color: C.dark }}>Cultural Videos</h2>
          </div>
          {(a.youtube_videos || []).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {a.youtube_videos.map((v, i) => (
                <div key={i} className="rounded-2xl overflow-hidden p-1 shadow-md" style={{ backgroundColor: C.pink }} data-testid={`video-${i}`}>
                  <div className="aspect-video rounded-xl overflow-hidden"><iframe src={`https://www.youtube.com/embed/${v.video_id}`} title={v.title || ''} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
                  {v.title && <div className="p-3 text-white"><h4 className="font-bold text-sm">{v.title}</h4></div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 rounded-2xl border-2 border-dashed" style={{ borderColor: `${C.pink}20` }}>
              <p style={{ color: `${C.dark}40` }}>No videos yet. Cultural videos will appear here.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer — dark textile */}
      <div className="py-2" style={textilePattern} />
      <footer className="py-16 text-center" style={{ backgroundColor: C.textile, color: C.white }}>
        <img src={a.logo_url || a.image_url} alt="" className="w-16 h-16 rounded-full mx-auto mb-4 object-cover" style={{ border: `3px solid ${C.pink}` }} />
        <h3 className="text-3xl font-black" style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.04em' }}>{a.short_name}</h3>
        <p className="text-white/70 text-sm mt-1 mb-2" style={{ fontFamily: "'Quicksand', sans-serif" }}>{a.name}</p>
        <p className="font-bold text-sm italic mb-6" style={{ fontFamily: "'Lora', serif", color: C.gold }}>Family, Heritage, Tradition, Progress</p>
        <div className="flex justify-center gap-6 text-sm text-white/40">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">CCA Montreal <ArrowUpRight className="w-3 h-3" /></Link>
          <Link to="/associations" className="hover:text-white transition-colors">All Associations</Link>
          <Link to="/calendar" className="hover:text-white transition-colors">Events</Link>
        </div>
      </footer>
    </div>
  );
};

export default StageTemplate;
